import React from 'react';
import { Conta, TipoContaId } from '../../types/financeiro';
import { sanitizeText } from '../../utils/textSanitizer';
import { ProtectedField, MaskedValue } from '../common/ProtectedField';
import { 
  Eye, 
  Edit, 
  Trash2, 
  CheckCircle2, 
  FileText, 
  Receipt, 
  Calendar, 
  AlertCircle, 
  Clock, 
  ArrowUpRight, 
  ArrowDownRight,
  Landmark,
  Building2 
} from 'lucide-react';

export interface FinanceiroContaCardProps {
  key?: React.Key;
  conta: Conta;
  pessoaNome: string;
  isReceita: boolean;
  onView: (c: Conta) => void;
  onEdit: (c: Conta) => void;
  onDelete: (c: Conta) => void;
  onBaixa: (c: Conta) => void;
  onViewComprovante: (c: Conta) => void;
  onEmitirNfse?: (c: Conta) => void;
}

export function FinanceiroContaCard({
  conta,
  pessoaNome,
  isReceita,
  onView,
  onEdit,
  onDelete,
  onBaixa,
  onViewComprovante,
  onEmitirNfse
}: FinanceiroContaCardProps) {
  const getStatus = (): 'atrasada' | 'aberta' | 'quitada' => {
    if (conta.dt_pagamento) return 'quitada';
    if (!conta.dt_vencimento) return 'aberta';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const vencimento = new Date(conta.dt_vencimento + 'T00:00:00');
    return vencimento < today ? 'atrasada' : 'aberta';
  };

  const status = getStatus();

  function formatDateBR(dateStr?: string | null): string {
    if (!dateStr) return '-';
    const parts = dateStr.split('T')[0].split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
  }

  const formatMoney = (val: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);

  const hasComprovante = Boolean(
    conta.comprovante_url || 
    conta.boleto_url || 
    conta.nf_url || 
    (conta.obs && conta.obs.includes('COMPROVANTE_URL:'))
  );

  return (
    <div className={`p-4 rounded-2xl border transition-all shadow-xs ${
      status === 'quitada'
        ? 'bg-white border-slate-200'
        : status === 'atrasada'
        ? 'bg-rose-50/40 border-rose-200'
        : 'bg-white border-slate-200'
    }`}>
      {/* Top row: ID, Badge Status & Amount */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-xs font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md border border-slate-200">
            #{conta.id}
          </span>
          {status === 'quitada' ? (
            <span className="bg-emerald-600 text-white px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider">
              {isReceita ? 'Recebida' : 'Quitada'}
            </span>
          ) : status === 'atrasada' ? (
            <span className="bg-rose-600 text-white px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider">
              Atrasada
            </span>
          ) : (
            <span className="bg-amber-500 text-white px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider">
              Em Aberto
            </span>
          )}
          {conta.numero_nf ? (
            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
              <Receipt className="w-3 h-3 text-emerald-600" />
              NFS-e: #{sanitizeText(conta.numero_nf)}
            </span>
          ) : isReceita && onEmitirNfse ? (
            <button
              type="button"
              id={`btn-card-gerar-nfse-${conta.id}`}
              onClick={(e) => {
                e.stopPropagation();
                onEmitirNfse(conta);
              }}
              className="inline-flex items-center gap-1 text-[11px] font-bold text-purple-700 bg-purple-50 hover:bg-purple-100 px-2 py-0.5 rounded border border-purple-200 transition-colors cursor-pointer"
              title="Emitir Nota Fiscal de Serviços (NFS-e)"
            >
              <Receipt className="w-3 h-3 text-purple-600" />
              <span>Gerar NFS-e</span>
            </button>
          ) : null}
          {conta.banco_nome && (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
              <Landmark className="w-3 h-3 text-blue-500" />
              {sanitizeText(conta.banco_nome)}
            </span>
          )}
          {conta.cnpj_faturador && (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-200" title={`Faturado por: ${conta.cnpj_faturador}`}>
              <Building2 className="w-3 h-3 text-indigo-500" />
              {conta.cnpj_faturador.includes('Voope Two') ? 'Voope Two' : (conta.cnpj_faturador.includes('23.059') ? 'Voope Matriz' : conta.cnpj_faturador)}
            </span>
          )}
        </div>

        {/* Valor em destaque com proteção de permissão */}
        <div className="text-right flex-shrink-0">
          <div className="text-base font-extrabold">
            <MaskedValue
              permission="view_financial_values"
              value={conta.valor}
              type="currency"
              className={isReceita ? 'text-emerald-600' : 'text-rose-600'}
            />
          </div>
          {conta.valor_pago && conta.valor_pago !== conta.valor && (
            <div className="text-[11px] text-slate-500 mt-0.5">
              Pago:{' '}
              <MaskedValue
                permission="view_financial_values"
                value={conta.valor_pago}
                type="currency"
              />
            </div>
          )}
        </div>
      </div>

      {/* Descrição & Pessoa */}
      <div className="mt-2.5">
        <h4 className="text-sm font-bold text-slate-900 line-clamp-1">
          {sanitizeText(conta.descricao) || 'Sem descrição'}
        </h4>
        <p className="text-xs text-slate-600 mt-0.5 flex items-center gap-1 font-medium">
          <span className="text-slate-400">{isReceita ? 'Cliente:' : 'Favorecido:'}</span>
          <span className="font-semibold text-slate-800 truncate">{sanitizeText(pessoaNome)}</span>
        </p>
      </div>

      {/* Datas: Vencimento & Pagamento */}
      <div className="grid grid-cols-2 gap-2 mt-3 pt-2.5 border-t border-slate-100 text-xs">
        <div>
          <span className="text-[11px] text-slate-400 block font-medium">Vencimento</span>
          <span className={`font-bold flex items-center gap-1 mt-0.5 ${status === 'atrasada' ? 'text-rose-700' : 'text-slate-700'}`}>
            <Calendar className="w-3.5 h-3.5 text-slate-400" />
            {formatDateBR(conta.dt_vencimento)}
          </span>
        </div>
        <div>
          <span className="text-[11px] text-slate-400 block font-medium">
            {isReceita ? 'Recebimento' : 'Pagamento'}
          </span>
          {conta.dt_pagamento ? (
            <span className="font-bold text-emerald-700 flex items-center gap-1 mt-0.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
              {formatDateBR(conta.dt_pagamento)}
            </span>
          ) : (
            <span className="text-slate-400 italic text-[11px] mt-0.5 block">Pendente</span>
          )}
        </div>
      </div>

      {/* Ações Mobile */}
      <div className="mt-3.5 pt-2.5 border-t border-slate-100 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          {hasComprovante && (
            <button
              type="button"
              onClick={() => onViewComprovante(conta)}
              title="Ver Documentos e Anexos"
              className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 rounded-lg text-xs font-bold transition-colors cursor-pointer"
            >
              <FileText className="w-3.5 h-3.5" />
              Anexos
            </button>
          )}
          <button
            type="button"
            onClick={() => onView(conta)}
            title="Visualizar Detalhes"
            className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
          >
            <Eye className="w-4 h-4" />
          </button>

          {/* Botão Emitir NFS-e para Contas a Receber */}
          {isReceita && onEmitirNfse && (
            <ProtectedField permission="manage_financial">
              <button
                type="button"
                onClick={() => onEmitirNfse(conta)}
                title={conta.numero_nf ? `NFS-e #${conta.numero_nf} já emitida` : "Emitir Nota Fiscal de Serviços (NFS-e)"}
                className={`p-1.5 rounded-lg transition-colors cursor-pointer ${
                  conta.numero_nf
                    ? 'text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50'
                    : 'text-purple-600 hover:text-purple-800 hover:bg-purple-50'
                }`}
              >
                <Receipt className="w-4 h-4" />
              </button>
            </ProtectedField>
          )}
          
          <ProtectedField permission="manage_financial">
            <button
              type="button"
              onClick={() => onEdit(conta)}
              title="Editar"
              className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
            >
              <Edit className="w-4 h-4" />
            </button>
          </ProtectedField>

          <ProtectedField permission="delete_financial">
            <button
              type="button"
              onClick={() => onDelete(conta)}
              title="Excluir"
              className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </ProtectedField>
        </div>

        {/* Botão Baixar Quitar */}
        <ProtectedField permission="pay_financial">
          <button
            type="button"
            onClick={() => onBaixa(conta)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all shadow-xs cursor-pointer ${
              conta.dt_pagamento
                ? 'bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-300'
                : isReceita
                ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                : 'bg-rose-600 text-white hover:bg-rose-700'
            }`}
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span>{conta.dt_pagamento ? 'Ver Baixa' : 'Dar Baixa'}</span>
          </button>
        </ProtectedField>
      </div>
    </div>
  );
}
